import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import { useSnackbar } from '@sketch-note/design-system/src/components/snackbar';
import { CloseIcon, CloudDownloadIcon, CsvIcon, DocumentIcon } from '@sketchnote/icons';
import classNames from 'classnames';
import { saveAs } from 'file-saver';
import prettySize from 'prettysize';
import * as React from 'react';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';

import { LoaderXs } from '../';
const { useState } = React;

interface FileUploadProps {
  acceptTypes?: string | string[];
  onDrop?: <T extends File>(
    acceptedFiles: T[],
    fileRejections: FileRejection[],
    event: DropEvent
  ) => void;
  className?: string;
  translations: {
    attachments_label: string;
    attachments_drop_label: string;
  };
  maxFileSize?: number;
  multiple?: boolean;
  disabled?: boolean;
  maxFiles?: number;
}

export const FileUpload = ({
  onDrop,
  acceptTypes,
  className,
  translations,
  maxFileSize = 1e7,
  multiple = false,
  maxFiles = 1,
  disabled,
}: FileUploadProps) => {
  const { getRootProps, getInputProps, isDragActive, isFileDialogActive, fileRejections } =
    useDropzone({
      onDrop,
      accept: acceptTypes,
      minSize: 0,
      maxSize: maxFileSize,
      multiple,
      maxFiles: maxFiles,
      disabled: disabled,
    });
  const [open] = useSnackbar();
  const classes = classNames({
    'flex items-center p-12 border-2 border-dashed border-gray-200 bg-gray-100 rounded text-gray-500 font-medium text-xs ':
      true,
    'text-primary-500 border-primary-500 justify-center': isDragActive || isFileDialogActive,
    'cursor-pointer': !disabled,
    [className || '']: !!className,
  });
  const isFileTooLarge = fileRejections.length > 0 && fileRejections[0].file.size > maxFileSize;

  React.useEffect(() => {
    if (isFileTooLarge) {
      open({
        text: 'File size too large!',
        snackType: 'error',
        position: 'top-center',
        duration: 2000,
      });
    }
  }, [isFileTooLarge]);

  return (
    <div {...(getRootProps({ className: classes }) as any)}>
      {isDragActive ? (
        <span>{translations.attachments_label}</span>
      ) : (
        <>
          <CloudDownloadIcon wrapperClass="mr-8" width="14" height="11.5" />
          <span>{translations.attachments_drop_label}</span>
        </>
      )}
      <input {...getInputProps()} disabled={disabled} />
    </div>
  );
};

export interface FileItemProps {
  className?: string;
  fileName?: string;
  fileSize?: number;
  onRemove?: () => void;
  isLoading?: boolean;
  filePath?: string;
  isReadOnly?: boolean;
}

export const FileItem = ({
  className,
  fileName,
  fileSize,
  onRemove,
  isLoading,
  filePath,
  isReadOnly = false,
}: FileItemProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const saveFile = (e) => {
    e.stopPropagation();
    setIsModalOpen(false);
    saveAs(filePath, fileName);
  };

  const extension = fileName && fileName.split('.').pop();

  const isFileImage =
    extension && ['jpeg', 'gif', 'png', 'apng', 'svg', 'bmp', 'bmp', 'ico'].includes(extension);

  const isModalOpenable = isFileImage;

  const openFileModal = () => {
    if (isModalOpenable) setIsModalOpen(true);
  };

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRemove?.();
  };

  return (
    <div
      className={classNames({
        'flex items-center justify-between py-13 px-16 border border-gray-200 bg-gray-100 rounded-md':
          true,
        'cursor-pointer': isModalOpenable,
        [className || '']: !!className,
      })}
      onClick={openFileModal}
    >
      <div className="flex items-center">
        {filePath ? (
          <div className="flex w-80 h-50 mr-10 max-w-80 max-h-50 min-w-80 min-h-50 place-content-center">
            {isFileImage ? (
              <img className="rounded-md w-full h-full object-fill " src={filePath} />
            ) : (
              <div className="flex w-full h-full mr-10 max-w-80 max-h-50 min-w-80 min-h-50 bg-gray-300 rounded-md place-content-center">
                <div className="flex text-blue-500 font-medium items-center">
                  {extension && extension.toUpperCase()}
                </div>
              </div>
            )}
          </div>
        ) : (
          <DocumentIcon wrapperClass="mr-15 text-lg text-gray-500" aria-label="document icon" />
        )}
        <div w-full>
          <p className="font-medium  max-w-144 truncate text-sm text-gray-999 pr-6">
            {fileName?.substr(0, 25)}
          </p>
          {!!fileSize && <p className="fon-medium text-xs text-gray-500">{prettySize(fileSize)}</p>}
        </div>
      </div>

      {isLoading ? (
        <LoaderXs className="text-xss" />
      ) : !isReadOnly ? (
        <div className="flex items-center">
          {filePath ? (
            <CloudDownloadIcon className="text-gray-400 mr-10 cursor-pointer" onClick={saveFile} />
          ) : (
            <></>
          )}
          {!!onRemove && (
            <button className="mr-4" aria-label="close button" onClick={handleRemove} type="button">
              <CloseIcon wrapperClass="text-gray-500" size={12} />
            </button>
          )}
        </div>
      ) : (
        <></>
      )}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalOverlay />
        <ModalContent
          maxWidth="80vw"
          height="80vh"
          borderRadius="0.8rem"
          className="bg-gray-100 static rounded-lg"
          margin="auto"
          paddingX={'10px'}
          paddingTop={'15px'}
        >
          <ModalCloseButton />
          <img
            src={filePath}
            className="m-auto max-w-full max-h-full"
            alt={fileName || 'Attachment'}
          />
        </ModalContent>
      </Modal>
    </div>
  );
};

export const CsvFileUpload = ({
  onDrop,
  acceptTypes,
  className,
  translations,
  disabled = false,
}: FileUploadProps) => {
  const { getRootProps, getInputProps, isDragActive, isFileDialogActive } = useDropzone({
    onDrop,
    accept: acceptTypes,
  });
  const classes = classNames({
    'flex items-center justify-center p-12 pt-30 pb-30 border-2 border-dashed border-gray-200 rounded text-gray-500 font-medium text-xs cursor-pointer':
      true,
    'text-primary-500 border-primary-500 justify-center': isDragActive || isFileDialogActive,
    [className || '']: !!className,
  });
  return (
    <div className="mt-8 ml-12 w-full">
      <div {...(getRootProps({ className: classes }) as any)}>
        {isDragActive ? (
          <span>{translations.attachments_label}</span>
        ) : (
          <>
            <CsvIcon wrapperClass="mr-8" width="50" height="50" />
          </>
        )}
        <input disabled={disabled} {...getInputProps()} />
      </div>
      <p className="text-sm text-center font-medium mt-8"> CSV </p>
      <p className="text-center text-xss mt-2"> Select a CSV file to import in Sketchnote </p>
    </div>
  );
};
